import type { ReactNode } from 'react';
import type { UserTypeValue } from '../data/collections';
import { UserType } from '../data/collections';
import { generatePath } from './routeUtils';
import { paths } from './paths';
import { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/loader/Loader';

type AuthStatus = 'LOADING' | 'AUTHORIZED' | 'REDIRECT_LOGIN' | 'REDIRECT_ACCESS_DENIED';

interface AuthState {
	status: AuthStatus;
	redirectPath?: string;
}

interface RouteGuardProps {
	children: ReactNode;
	allowedRoles?: UserTypeValue[];
	permissions?: string[];
	allowUnauthed?: boolean;
}

export const RouteGuard = ({ children, allowedRoles = [], permissions = [], allowUnauthed = false }: RouteGuardProps): ReactNode => {
	const { user, member, role, loading } = useAuth();
	const location = useLocation();

	const authState = useMemo((): AuthState => {
		const isStillLoading = loading || (user && !user.isAnonymous && !role);
		if (isStillLoading) return { status: 'LOADING' };
		if (!user || user.isAnonymous) return allowUnauthed ? { status: 'AUTHORIZED' } : { status: 'REDIRECT_LOGIN' };

		let isAuthorized = allowedRoles.length === 0 || allowedRoles.includes(role!);

		if (isAuthorized && permissions.length > 0 && member) {
			const checkPermission = (permKey: string): boolean => {
				const keys = permKey.split('.');
				let currentPerm: Record<string, unknown> | unknown = member.permissions;
				for (const key of keys) {
					currentPerm = (currentPerm as Record<string, unknown>)?.[key];
					if (currentPerm === undefined) return false;
				}
				return currentPerm === true;
			};

			const hasAdminPermission = checkPermission('admin');
			const hasAllRequiredPermissions = permissions.every(checkPermission);

			isAuthorized = hasAdminPermission || hasAllRequiredPermissions;
		}

		if (isAuthorized) return { status: 'AUTHORIZED' };

		const isAdminRoute = location.pathname.startsWith('/members') || location.pathname.startsWith('/applicants') || location.pathname.startsWith('/applications');
		return {
			status: 'REDIRECT_ACCESS_DENIED',
			redirectPath: generatePath(isAdminRoute ? paths.adminAccessDenied : paths.caAccessDenied),
		};
	}, [user, member, role, loading, allowedRoles, permissions, location, allowUnauthed]);

	if (authState.status === 'LOADING') return <Loader />;
	if (authState.status === 'AUTHORIZED') return children;
	if (authState.status === 'REDIRECT_LOGIN') return <Navigate to={generatePath(paths.login)} state={{ from: location }} replace />;
	if (authState.status === 'REDIRECT_ACCESS_DENIED') return <Navigate to={authState.redirectPath!} state={{ from: location }} replace />;

	return <Loader />;
};

interface ChildrenProps {
	children: ReactNode;
}

export const ApplicantsOnly = ({ children }: ChildrenProps): ReactNode => {
	const { user, role, applicant, loading } = useAuth();
	if (loading) return <Loader />;
	if (!user || (role !== UserType.applicant && role !== UserType.both) || !applicant) {
		return <Navigate to={generatePath(paths.redirect)} replace />;
	}
	return children;
};

export const MembersOnly = ({ children }: ChildrenProps): ReactNode => {
	const { user, member, role, loading } = useAuth();
	if (loading) return <Loader />;
	if (!user || (role !== UserType.member && role !== UserType.both) || !member) {
		return <Navigate to={generatePath(paths.redirect)} replace />;
	}
	return children;
};

export const Protected = ({ children }: ChildrenProps): ReactNode => {
	const { user, loading } = useAuth();
	if (loading) return <Loader />;
	if (!user) {
		return <Navigate to={generatePath(paths.login)} replace />;
	}
	return children;
};

export const Unprotected = ({ children }: ChildrenProps): ReactNode => {
	const { user, loading } = useAuth();
	if (loading) return <Loader />;
	if (user) {
		return <Navigate to={generatePath(paths.redirect)} replace />;
	}
	return children;
};
